import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Loader2, Printer, CreditCard, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicQuote() {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef();

  const cancelled = searchParams.get('cancelled');

  useEffect(() => {
    fetchQuote();
  }, [quoteId]);

  useEffect(() => {
    if (cancelled) {
      toast.error('Payment was cancelled');
    }
  }, [cancelled]);

  const fetchQuote = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/quote/${quoteId}`);
      if (response.ok) {
        const data = await response.json();
        setQuote(data);
      } else {
        const err = await response.json();
        setError(err.detail || 'Quote not found');
      }
    } catch (err) {
      setError('Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAndPay = async () => {
    setAccepting(true);
    try {
      const response = await fetch(`${API_URL}/api/public/quote/${quoteId}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to process');
        setAccepting(false);
      }
    } catch (err) {
      toast.error('Failed to process');
      setAccepting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Quote Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </Card>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const isAccepted = quote.status === 'accepted' || quote.status === 'converted';
  const isExpired = quote.valid_until && new Date(quote.valid_until) < new Date();

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      {/* Action Bar - Hidden in print */}
      <div className="max-w-4xl mx-auto mb-6 flex justify-between items-center print:hidden">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quote</h1>
          <p className="text-gray-600">#{quote.quote_number}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          {quote.can_accept && !isExpired && (
            <Button onClick={handleAcceptAndPay} disabled={accepting} className="bg-green-600 hover:bg-green-700">
              {accepting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Accept & Pay {formatCurrency(quote.total)}
            </Button>
          )}
        </div>
      </div>

      {/* Quote Document */}
      <div ref={printRef} className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none">
        {/* Header */}
        <div className={`px-8 py-6 ${isAccepted ? 'bg-green-600' : 'bg-cyan-600'} text-white`}>
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold">{quote.business.name}</h2>
              {quote.business.email && (
                <p className="text-cyan-100 mt-1">{quote.business.email}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-cyan-100 text-sm uppercase tracking-wider">Quote</p>
              <p className="text-2xl font-bold">#{quote.quote_number}</p>
              {isAccepted && (
                <span className="inline-flex items-center gap-1 mt-2 bg-white/20 px-3 py-1 rounded-full text-sm">
                  <CheckCircle className="h-4 w-4" />
                  ACCEPTED
                </span>
              )}
              {isExpired && !isAccepted && (
                <span className="inline-flex items-center gap-1 mt-2 bg-red-500/80 px-3 py-1 rounded-full text-sm">
                  <Clock className="h-4 w-4" />
                  EXPIRED
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="px-8 py-6 border-b">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Prepared For</p>
              <p className="font-semibold text-gray-900">{quote.client.name}</p>
              {quote.client.email && <p className="text-gray-600">{quote.client.email}</p>}
              {quote.client.address && <p className="text-gray-600">{quote.client.address}</p>}
            </div>
            <div className="text-right">
              <div className="mb-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Quote Date</p>
                <p className="text-gray-900">{formatDate(quote.date)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Valid Until</p>
                <p className={`font-semibold ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                  {formatDate(quote.valid_until)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="px-8 py-6">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 text-xs text-gray-500 uppercase tracking-wider">Description</th>
                <th className="text-center py-3 text-xs text-gray-500 uppercase tracking-wider">Qty</th>
                <th className="text-right py-3 text-xs text-gray-500 uppercase tracking-wider">Rate</th>
                <th className="text-right py-3 text-xs text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody>
              {quote.items.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-4 text-gray-900">{item.description}</td>
                  <td className="py-4 text-center text-gray-600">{item.quantity}</td>
                  <td className="py-4 text-right text-gray-600">{formatCurrency(item.unit_price)}</td>
                  <td className="py-4 text-right text-gray-900 font-medium">{formatCurrency(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="px-8 py-6 bg-gray-50">
          <div className="flex justify-end">
            <div className="w-64">
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900">{formatCurrency(quote.subtotal)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Tax</span>
                <span className="text-gray-900">{formatCurrency(quote.tax)}</span>
              </div>
              <div className={`flex justify-between py-3 border-t-2 ${isAccepted ? 'border-green-500' : 'border-cyan-500'}`}>
                <span className="text-lg font-bold text-gray-900">Total</span>
                <span className={`text-lg font-bold ${isAccepted ? 'text-green-600' : 'text-cyan-600'}`}>
                  {formatCurrency(quote.total)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {quote.notes && (
          <div className="px-8 py-6 border-t">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Notes</p>
            <p className="text-gray-600">{quote.notes}</p>
          </div>
        )}

        {/* Terms */}
        {quote.terms && (
          <div className="px-8 py-6 border-t">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Terms & Conditions</p>
            <p className="text-gray-600 text-sm">{quote.terms}</p>
          </div>
        )}

        {/* Accept Button - Print Hidden */}
        {quote.can_accept && !isExpired && (
          <div className="px-8 py-6 bg-cyan-50 border-t print:hidden">
            <div className="text-center">
              <Button 
                size="lg" 
                onClick={handleAcceptAndPay} 
                disabled={accepting}
                className="bg-green-600 hover:bg-green-700 text-lg px-8"
              >
                {accepting ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="h-5 w-5 mr-2" />
                )}
                Accept & Pay - {formatCurrency(quote.total)}
              </Button>
              <p className="text-sm text-gray-500 mt-2">Secure payment powered by Stripe</p>
            </div>
          </div>
        )}

        {isExpired && !isAccepted && (
          <div className="px-8 py-6 bg-red-50 border-t print:hidden">
            <div className="text-center">
              <p className="text-red-600 font-medium">This quote has expired</p>
              <p className="text-sm text-gray-500 mt-1">Please contact {quote.business.name} for an updated quote</p>
            </div>
          </div>
        )}
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
